import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import i18n from 'i18next'
import { useDispatch } from 'react-redux'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'

import { useTranslations, useAuth } from '@app/hooks'

import { Loader } from '@lib/components'

import { AppRoute } from './AppRoute'
import { checkRefreshToken } from './api/auth/checkRefreshToken'
import { registerAppInit } from './appInit'
import config from './config'
import { APP_ROUTES } from './constants'
import { ChatProvider } from './providers'

import { getTokenFromId } from './services'
import { AppDispatch, resetAppState, store } from './store'
import { getTokenAccountId, getTokenUserId, refreshTokenThunk } from './store/auth'

import { getConfigurationThunk } from './store/configuration'
import { getCountriesThunk } from './store/countries'
import { getLanguagesThunk } from './store/languages'
import { getTranslationsThunk, setTranslationsReady } from './store/translation'
import { getCurrentUserThunk } from './store/user'
import { setBacktrack } from './utils'
import { getAuthMode } from './utils'
import {
  getActiveAuthTokenFromCookies,
  getAuthSessionKey,
  getAuthSessionKeyFromCookies,
} from './utils/token'
import { getURLTokenHashFromQuery } from './utils/url/getURLTokenHashFromQuery'
import { hasFeature, WebViewFeature } from './utils/webview/hasFeature'

export const AppContainer = () => {
  const dispatch: AppDispatch = useDispatch()

  const authState = useAuth()

  const { ready } = useTranslations()

  const [isInitialized, setIsInitialized] = useState(false)
  const [appInstance, setAppInstance] = useState(0)
  const skipNextTokenInitRef = useRef(false)
  const syncingExternalSessionRef = useRef(false)
  const buyerTokenMode = getAuthMode().buyerTokenMode

  const authSessionKey = useMemo(
    () =>
      getAuthSessionKey({
        token: authState.tokenData.token,
        urlToken: authState.tokenData.urlToken,
        buyerTokenMode,
      }),
    [
      authState.tokenData.token,
      authState.tokenData.urlToken,
      buyerTokenMode,
    ]
  )

  const init = useCallback(async () => {
    const authState = store.getState().auth
    const currentUserId = getTokenUserId(authState)
    const currentAccountId = getTokenAccountId(authState)
    const { b2bView, buyerView, buyerTokenMode } = getAuthMode()

    if (!config.identity.identityUrl) {
      await dispatch(getConfigurationThunk())
    }

    const urlTokenHash = getURLTokenHashFromQuery()
    const { urlToken } = authState.tokenData

    if (
      urlTokenHash &&
      urlTokenHash != urlToken &&
      b2bView
    ) {
      return await getTokenFromId()
    }

    const canDelegateJwtExchange =
      config.identity.isWebView && hasFeature(WebViewFeature.DelegatedJwtExchange)

    const doRefreshToken =
      !authState.tokenData.token && !b2bView
        ? buyerView && !buyerTokenMode
          ? false
          : await checkRefreshToken()
        : false

    if (
      !authState.tokenData.token &&
      (authState.tokenData.refresh_token || doRefreshToken || canDelegateJwtExchange)
    ) {
      await dispatch(refreshTokenThunk())
      return
    }

    if (currentUserId && currentAccountId) {
      await dispatch(
        getCurrentUserThunk({
          userId: currentUserId,
          accountId: currentAccountId,
        })
      )

      const state = store.getState()

      const onInit = () => {
        dispatch(setTranslationsReady())
        i18n.off('initialized', onInit)
      }

      if (!state.translation.ready) {
        if (i18n.isInitialized) {
          onInit()
        } else {
          i18n.on('initialized', onInit)
        }

        dispatch(getTranslationsThunk())
      }

      if (!state.countries.ready) {
        dispatch(getCountriesThunk())
      }

      if (!state.languages.ready) {
        dispatch(getLanguagesThunk())
      }

      setIsInitialized(true)
    } else {
      await getTokenFromId()
    }
  }, [
    dispatch,
  ])

  const reinitializeApp = useCallback(async () => {
    if (!getActiveAuthTokenFromCookies()) {
      return
    }
    skipNextTokenInitRef.current = true
    setIsInitialized(false)
    resetAppState()
    setBacktrack()
    setAppInstance((prev) => prev + 1)
    await init()
  }, [init])

  const syncSessionFromCookies = useCallback(async () => {
    if (syncingExternalSessionRef.current) {
      return
    }

    const cookieSessionKey = getAuthSessionKeyFromCookies()

    if (cookieSessionKey === authSessionKey) {
      return
    }

    syncingExternalSessionRef.current = true

    try {
      await reinitializeApp()
    } finally {
      syncingExternalSessionRef.current = false
    }
  }, [authSessionKey, dispatch, reinitializeApp])

  useEffect(() => {
    registerAppInit(reinitializeApp)
  }, [reinitializeApp])

  useEffect(() => {
    const onFocus = () => {
      void syncSessionFromCookies()
    }

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void syncSessionFromCookies()
      }
    }

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [syncSessionFromCookies])

  useEffect(() => {
    if (skipNextTokenInitRef.current) {
      skipNextTokenInitRef.current = false
      return
    }

    setBacktrack()
    setIsInitialized(false)
    init()
  }, [authSessionKey, init])

  if (!ready || !isInitialized) {
    return <Loader />
  }

  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <Routes>
        {APP_ROUTES.map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ChatProvider key={appInstance}>
                <AppRoute />
              </ChatProvider>
            }
          />
        ))}
      </Routes>
    </Router>
  )
}
